import { logWarn } from "@/lib/log";

// Reverse-geocode [lat, lng] → { city, region, country }. Browser-side, no API key:
// 1) OpenStreetMap Nominatim, 2) BigDataCloud client endpoint, 3) Photon (komoot). Cached per ~1 km + language.
const CACHE_KEY = "geo_place_cache";
const readCache = () => { try { return JSON.parse(localStorage.getItem(CACHE_KEY)) || {}; } catch { return {}; } };
const nonEmpty = (p) => (p && (p.city || p.country) ? p : null);

async function viaNominatim(lat, lng, lang) {
  const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=10&accept-language=${encodeURIComponent(lang)}`;
  const r = await fetch(url, { headers: { Accept: "application/json" } });
  if (!r.ok) throw new Error(`nominatim ${r.status}`);
  const a = (await r.json()).address || {};
  return nonEmpty({
    city: a.city || a.town || a.municipality || a.city_district || a.county || a.state_district || a.village || a.state || "",
    region: a.state || a.region || "",
    country: a.country || "",
  });
}

async function viaBigDataCloud(lat, lng, lang) {
  const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=${encodeURIComponent(lang)}`;
  const r = await fetch(url, { headers: { Accept: "application/json" } });
  if (!r.ok) throw new Error(`bigdatacloud ${r.status}`);
  const d = await r.json();
  return nonEmpty({ city: d.city || d.locality || d.principalSubdivision || "", region: d.principalSubdivision || "", country: d.countryName || "" });
}

const PHOTON_LANGS = new Set(["en", "de", "fr"]);
async function viaPhoton(lat, lng, lang) {
  const r = await fetch(`https://photon.komoot.io/reverse?lon=${lng}&lat=${lat}&lang=${PHOTON_LANGS.has(lang) ? lang : "en"}`, { headers: { Accept: "application/json" } });
  if (!r.ok) throw new Error(`photon ${r.status}`);
  const p = (await r.json()).features?.[0]?.properties || {};
  return nonEmpty({ city: p.city || p.town || p.village || p.county || p.state || "", region: p.state || p.county || "", country: p.country || "" });
}

export async function reversePlace([lat, lng], lang = "en") {
  const key = `${lat.toFixed(2)},${lng.toFixed(2)},${lang}`;
  const cache = readCache();
  if (cache[key]) return cache[key];
  let place = null;
  for (const provider of [viaNominatim, viaBigDataCloud, viaPhoton]) {
    try { place = await provider(lat, lng, lang); } catch (e) { logWarn("reverse geocode provider failed:", e); }
    if (place) break;
  }
  if (!place) return null;
  const entries = Object.entries({ ...cache, [key]: place }).slice(-20);
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(Object.fromEntries(entries))); } catch (e) { logWarn("place cache not saved:", e); }
  return place;
}

export const placeLabel = (p) => (p ? [p.city, p.region && p.region !== p.city ? p.region : null, p.country].filter(Boolean).join(", ") : "");
